// SF4 — True Demand & Lost Sales.
//
// Velocity measured against the days a product was actually BUYABLE, not
// against calendar days. The difference is not academic: dividing by calendar
// days makes anything that stocked out look like a slow seller, so the restock
// built on it under-orders, the product runs out again, and the next figure is
// lower still. This page exists to break that loop.
//
// Projected and lost units are ESTIMATES built on an observed rate, and are
// styled to read differently from measured figures — the PRD is explicit that
// the two must not look alike.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../GeographicSales.module.css';
import { getTrueDemand } from '../services/geographicSalesApi';
import SyncProgress from '../../components/SyncProgress';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const REASONS = {
  out_of_stock: 'Out of stock',
  stranded: 'Stranded',
  suppressed_listing: 'Suppressed listing',
  promoted: 'On deal',
  bulk: 'Bulk order',
};

// A fortnight of days as a strip: how much of the window was actually sellable.
// Reads faster than four numbers, and the shape is the point.
const DayBar = ({ days }) => {
  const total = days.total || 1;
  const segments = [
    { key: 'clean', width: days.clean, className: local.dayClean, label: 'buyable' },
    { key: 'suppressed', width: days.suppressed, className: local.daySuppressed, label: 'could not be bought' },
    { key: 'inflated', width: days.inflated, className: local.dayInflated, label: 'demand distorted' },
    { key: 'unknown', width: days.unknown, className: local.dayUnknown, label: 'not known' },
  ].filter((segment) => segment.width > 0);
  return (
    <span className={local.dayBar} title={segments.map((s) => `${s.width}d ${s.label}`).join(', ')}>
      {segments.map((segment) => (
        <span key={segment.key} className={segment.className}
          style={{ width: `${(segment.width / total) * 100}%` }} />
      ))}
    </span>
  );
};

const TrueDemand = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getTrueDemand(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const rows = data?.rows || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>True demand and lost sales</h2>
        {data?.totals?.lost_units > 0 && (
          <span className={local.estimate}>
            ≈{inr.format(Math.round(data.totals.lost_units))} units lost
            across {data.totals.products_with_loss} products
          </span>
        )}
      </div>

      <p className={local.muted}>
        Velocity here divides by the days a product could actually be bought, not
        by calendar days. A day it was buyable and sold nothing still counts —
        that is real demand information.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.progress ? 'Sync in progress'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load demand'}
            </strong>
            <div>{error.message}</div>
            <SyncProgress progress={error.progress} />
          </div>
        </div>
      )}

      {/* Without stock history this is calendar-day velocity under another
          name, so the surface refuses rather than misleading. */}
      {!error && data && data.available === false && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiInfo className={local.bannerIcon} />
          <div><strong>No buyability history yet</strong><div>{data.reason}</div></div>
        </div>
      )}

      {!error && data?.available && (
        <>
          {data.measured && (
            <div className={local.breadcrumb}>
              <span className={local.crumbTotal}>
                Measured over {data.measured.start} to {data.measured.end}
                {' — as far as the stock history reaches'}
              </span>
            </div>
          )}
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th />
                  <th>Product</th>
                  <th className={local.numeric}>Velocity /day</th>
                  <th>Window</th>
                  <th className={local.numeric}>Sold</th>
                  <th className={local.numeric}>Would have sold</th>
                  <th className={local.numeric}>Lost</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td className={local.emptyCell} colSpan={7}>
                      {loading ? 'Loading…' : 'No products with enough buyable days yet.'}
                    </td>
                  </tr>
                ) : rows.map((row) => (
                  <React.Fragment key={row.asin}>
                    <tr className={local.drillable}
                      onClick={() => setOpen(open === row.asin ? null : row.asin)}>
                      <td className={local.rankCell}>
                        {open === row.asin ? <FiChevronDown /> : <FiChevronRight />}
                      </td>
                      <td>{row.asin}</td>
                      <td className={local.numeric}>
                        {row.velocity === null
                          ? <span className={local.blank} title={row.velocity_reason}>—</span>
                          : row.velocity.toFixed(2)}
                      </td>
                      <td><DayBar days={row.days} /></td>
                      <td className={local.numeric}>{inr.format(row.actual_units)}</td>
                      <td className={local.numeric}>
                        {row.projected_units === null ? <span className={local.blank}>—</span>
                          : <span className={local.estimate}>
                            ≈{inr.format(Math.round(row.projected_units))}
                          </span>}
                      </td>
                      <td className={local.numeric}>
                        {!row.lost_units ? <span className={local.blank}>—</span>
                          : <span className={local.lost}>
                            ≈{inr.format(Math.round(row.lost_units))}
                          </span>}
                      </td>
                    </tr>

                    {open === row.asin && (
                      <tr className={local.nested}>
                        <td />
                        <td colSpan={6}>
                          <div className={local.reasonRow}>
                            {Object.entries(row.by_reason || {}).length === 0
                              ? 'Buyable every day in the measured window.'
                              : Object.entries(row.by_reason).map(([reason, count]) => (
                                <span key={reason} className={local.reasonChip}>
                                  {REASONS[reason] || reason}: {count}d
                                </span>
                              ))}
                          </div>
                          {row.lost_by_region?.length > 0 && (
                            <div className={local.reasonRow}>
                              <span className={local.muted}>
                                Where the loss lands, split by where this product
                                actually sells:
                              </span>
                              {row.lost_by_region.map((region) => (
                                <span key={region.region} className={local.reasonChip}>
                                  {region.region} ≈{region.lost_units}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default TrueDemand;
